import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Clock, GraduationCap, KeyRound, User, Hash } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function PupilAuth() {
  const [mode, setMode] = useState("join");
  const [form, setForm] = useState({ join_code: "", name: "", pin: "", year_group: "", sub_group: "", parent_email: "" });
  const [yearGroups, setYearGroups] = useState(null);
  const [subGroups, setSubGroups] = useState({});
  const [busy, setBusy] = useState(false);
  const { saveSession } = useAuth();
  const navigate = useNavigate();

  const fetchYearGroups = async (code) => {
    const c = code.trim().toUpperCase();
    if (c.length < 6) {
      setYearGroups(null);
      return;
    }
    try {
      const { data } = await api.get(`/public/school/${c}/year-groups`);
      setYearGroups(data.year_groups);
      setSubGroups(data.sub_groups || {});
      toast.success(`School found: ${data.school_name}`);
    } catch {
      setYearGroups(null);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { parent_email, ...rest } = form;
      const payload = mode === "join"
        ? { ...rest, ...(parent_email.trim() ? { parent_email: parent_email.trim() } : {}) }
        : { join_code: form.join_code, name: form.name, pin: form.pin };
      if (mode === "join" && !form.year_group) {
        toast.error("Please choose your year group");
        setBusy(false);
        return;
      }
      const endpoint = mode === "join" ? "/auth/pupil/join" : mode === "teacher" ? "/auth/teacher/login" : "/auth/pupil/login";
      const { data } = await api.post(endpoint, payload);
      saveSession(data);
      toast.success(`Hi ${data.user.name}! Welcome to ${data.user.school_name}.`);
      navigate(mode === "teacher" ? "/teacher" : "/pupil");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const DEFAULT_YEARS = ["Year 7", "Year 8", "Year 9", "Year 10", "Year 11"];

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#A7E4D3]/40" data-testid="pupil-auth-page">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8" data-testid="pupil-auth-brand">
          <div className="w-9 h-9 rounded-xl border-2 border-zinc-900 bg-white flex items-center justify-center shadow-[3px_3px_0px_0px_#18181B]">
            <Clock className="w-5 h-5" />
          </div>
          <span className="font-display font-black text-2xl">MyTimetable.co.uk</span>
        </Link>

        <div className="nb-card p-8 bg-white">
          <div className="text-center mb-6">
            <div className="w-14 h-14 mx-auto rounded-xl border-2 border-zinc-900 bg-[#FDE68A] flex items-center justify-center shadow-[3px_3px_0px_0px_#18181B]">
              <GraduationCap className="w-7 h-7" />
            </div>
            <h1 className="font-display font-black text-2xl mt-4" data-testid="pupil-auth-heading">
              {mode === "join" ? "Join your school" : mode === "teacher" ? "Staff login" : "Welcome back!"}
            </h1>
            <p className="text-sm text-zinc-500 mt-1">
              {mode === "join" ? "Your teacher gave you a join code. Pop it in below." : mode === "teacher" ? "Your school office gave you a PIN. Enter the join code, your name and PIN." : "Enter your school join code, name and PIN."}
            </p>
          </div>

          <div className="flex rounded-xl border-2 border-zinc-900 overflow-hidden mb-6" role="tablist">
            {[["join", "First time"], ["login", "Log in"], ["teacher", "Teacher"]].map(([m, label]) => (
              <button key={m} type="button" role="tab" data-testid={`pupil-${m}-tab`}
                onClick={() => setMode(m)}
                className={`flex-1 py-2.5 font-display font-bold text-sm transition-colors duration-150 ${mode === m ? "bg-[#A7E4D3]" : "bg-white text-zinc-700 hover:bg-zinc-50"}`}>
                {label}
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="nb-label" htmlFor="join-code">School join code</label>
              <div className="relative mt-2">
                <Hash className="w-4 h-4 absolute left-3 top-3.5 text-zinc-400" />
                <input id="join-code" data-testid="pupil-join-code-input" className="nb-input pl-9 uppercase tracking-widest" placeholder="e.g. K7XQ2P"
                  value={form.join_code}
                  onChange={(e) => { setForm({ ...form, join_code: e.target.value.toUpperCase() }); fetchYearGroups(e.target.value); }}
                  required maxLength={8} />
              </div>
            </div>
            <div>
              <label className="nb-label" htmlFor="pupil-name">Your name</label>
              <div className="relative mt-2">
                <User className="w-4 h-4 absolute left-3 top-3.5 text-zinc-400" />
                <input id="pupil-name" data-testid="pupil-name-input" className="nb-input pl-9" placeholder="e.g. Sophie"
                  value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required minLength={2} />
              </div>
            </div>
            {mode === "join" && (
              <>
                <div>
                  <label className="nb-label" htmlFor="pupil-year-group">Year group {yearGroups ? "" : "(enter your join code first)"}</label>
                  <select id="pupil-year-group" data-testid="pupil-year-group-select" className="nb-input mt-2"
                    value={form.year_group} onChange={(e) => setForm({ ...form, year_group: e.target.value, sub_group: "" })} required disabled={!yearGroups}>
                    <option value="">{yearGroups ? "Choose your year…" : "Enter your join code above…"}</option>
                    {(yearGroups || DEFAULT_YEARS).map((y) => <option key={y} value={y}>{y}</option>)}
                  </select>
                </div>
                {form.year_group && (subGroups[form.year_group] || []).length > 0 && (
                  <div>
                    <label className="nb-label" htmlFor="pupil-sub-group">Sub-group (optional)</label>
                    <select id="pupil-sub-group" data-testid="pupil-sub-group-select" className="nb-input mt-2"
                      value={form.sub_group} onChange={(e) => setForm({ ...form, sub_group: e.target.value })}>
                      <option value="">No sub-group</option>
                      {subGroups[form.year_group].map((g) => <option key={g} value={g}>{g}</option>)}
                    </select>
                  </div>
                )}
                <div>
                  <label className="nb-label" htmlFor="pupil-parent-email">Parent email (optional, for notices)</label>
                  <input id="pupil-parent-email" type="email" data-testid="pupil-parent-email-input" className="nb-input mt-2" placeholder="parent@example.com"
                    value={form.parent_email} onChange={(e) => setForm({ ...form, parent_email: e.target.value })} />
                </div>
              </>
            )}
            <div>
              <label className="nb-label" htmlFor="pupil-pin">{mode === "join" ? "Choose a PIN (4–8 digits)" : "Your PIN"}</label>
              <div className="relative mt-2">
                <KeyRound className="w-4 h-4 absolute left-3 top-3.5 text-zinc-400" />
                <input id="pupil-pin" type="password" inputMode="numeric" data-testid="pupil-pin-input" className="nb-input pl-9" placeholder="••••"
                  value={form.pin} onChange={(e) => setForm({ ...form, pin: e.target.value })} required minLength={4} maxLength={8} />
              </div>
            </div>
            <button type="submit" disabled={busy} data-testid="pupil-auth-submit" className="nb-btn w-full bg-[#A7E4D3] py-3">
              {busy ? "One moment…" : mode === "join" ? "Join & build my timetable" : "Log in"}
            </button>
          </form>
        </div>

        <p className="text-center text-sm text-zinc-600 mt-6">
          A school? <Link to="/school/auth" className="font-bold text-[#1E3A8A] underline" data-testid="goto-school-auth">School login</Link>
          <span className="mx-2 text-zinc-300">·</span>
          Family code? <Link to="/kid/auth" className="font-bold text-[#1E3A8A] underline" data-testid="goto-kid-auth">Kid login</Link>
        </p>
      </div>
    </div>
  );
}
